import {requestApi} from '@/utils/httpClient';
import {requestManager} from '@/utils/ky-hooks/requestManager';

// 定义请求参数类型
interface JobParams {
    job_id: string;
    stage?: string;
}

/**
 * 启动新任务的接口示例(ky版本)
 * @param jobId
 * @returns Promise直接返回响应数据
 */
export async function startJob(jobId: string): Promise<ApiResponse> {
    return requestApi
        .post('console/v1/jobs/start', {job_id: jobId}, {timeout: 5 * 60 * 1000});
}

// 进度查询接口
export async function fetchJobStatus(jobId: string): Promise<ApiResponse> {
    return requestManager.execute(
        `fetchJobStatus_${jobId}`, // 唯一的请求 key
        signal => requestApi.get('console/v1/jobs/status', {job_id: jobId}, {signal})
    );
}

// 任务取消接口
export async function cancelJob(params: {job_id: string, release: boolean}): Promise<ApiResponse> {
    return requestApi.post('console/v1/jobs/cancel', params);
}

// 任务终止接口
export async function terminateJob(params: JobParams): Promise<ApiResponse> {
    return requestApi.post('console/v1/jobs/stop', params);
}

// 任务暂停接口
export async function pauseJob(params: JobParams): Promise<ApiResponse> {
    return requestManager.execute(
        `pauseJob_${params.job_id}`,
        signal => requestApi.post('console/v1/jobs/pause', params, {timeout: 5 * 60 * 1000, signal})
    );
}

export async function continueJob(params: JobParams): Promise<ApiResponse> {
    return requestManager.execute(
        `continueJob_${params.job_id}`,
        signal => requestApi.post('console/v1/jobs/resume', params, {timeout: 5 * 60 * 1000, signal})
    );
}

// 任务下一步接口 进入演化阶段
export async function nextJob(params: JobParams): Promise<ApiResponse> {
    return requestManager.execute(
        `nextJob_${params.job_id}`,
        signal => requestApi.post('console/v1/jobs/confirm',
            {job_id: params.job_id, stage: params.stage},
            {signal}
        )
    );
}

// 任务重新构造
export async function rebuildJob(job_id: string): Promise<ApiResponse> {
    return requestManager.execute(
        `rebuildJob_${job_id}`,
        signal => requestApi.post('console/v1/jobs/restart', {job_id}, {signal})
    );
}


// 获取webIDE/Report url接口
export async function fetchJobReport(jobId: string): Promise<ApiResponse> {
    return requestManager.execute(
        `fetchJobReport_${jobId}`,
        signal => requestApi.get('console/v1/jobs/web_ide_url', {job_id: jobId}, {signal})
    );
}

// 任务done接口
export async function doneJob(jobId: string): Promise<ApiResponse> {
    return requestManager.execute(
        `doneJob_${jobId}`,
        signal => requestApi.post('console/v1/jobs/done', {job_id: jobId}, {signal})
    );
}

// 获取日志
export async function fetchJobLog(params: any, extraOptions: any): Promise<any> {
    return requestApi.getStream('console/v1/jobs/log_tail', params, extraOptions);
}

// 日志查询
export async function searchLog(params: any): Promise<ApiResponse> {
    return requestApi.get('console/v1/jobs/log_search', params);
}

// TODO:项目相关

// 获取项目列表
export async function fetchProjectsList(user_id: string): Promise<ApiResponse> {
    return requestApi.get('console/v1/projects/list', {user_id: user_id});
}
/**
 * 获取任务列表
 * @param params 查询参数，包含项目ID等过滤条件
 * @returns 返回任务列表数据
 */
export async function fetchJobsList(params: {job_status?: string | ''}): Promise<ApiResponse> {
    return requestApi.get(`console/v1/jobs/list?job_status=${params.job_status}`);
}

// 创建任务
export async function createJob(params: {name: string}): Promise<ApiResponse> {
    return requestApi.post('console/v1/jobs/create', params);
}

// 删除任务
export async function deleteJob(job_id: string): Promise<ApiResponse> {
    return requestApi.delete('console/v1/jobs/delete', {job_id: job_id});
}

// 更新任务，启动后修改任务配置
export async function updateJob(params: UpdateJobParams): Promise<ApiResponse> {
    return requestManager.execute(
        `updateJob_${params.job_id}`,
        signal => requestApi.put('console/v1/jobs/update_config', params, {signal})
    );
}

// 获取任务详情
export async function getJobDetail(job_id: string): Promise<ApiResponse> {
    return requestManager.execute(
        `getJobDetail_${job_id}`,
        signal => requestApi.get('console/v1/jobs/detail', {job_id}, {signal}),
        {isManual: true}
    );
}

// 任务编辑 更新任务 定时save
export async function putSaveEditJob(params: UpdateJobParams): Promise<ApiResponse> {
    return requestApi.post('console/v1/jobs/save', params);
}

// coding agent调整生成
export async function codingAgentAdjust(params: any): Promise<ApiResponse> {
    return requestApi.post('console/v1/jobs/coding_agent/finetuning', params);
}

// coding agent停止生成
export async function codeAgentStop(params: JobParams): Promise<ApiResponse> {
    return requestApi.post('console/v1/jobs/coding_agent/stop', params);
}

// coding agent使用初始解
export async function codeAgentChose(params: any): Promise<ApiResponse> {
    return requestApi.post('console/v1/jobs/coding_agent/chose', params);
}


/**
 * code agent显示初始解
 */
export async function codeAgentInitAnswer(params: any): Promise<ApiResponse> {
    return requestManager.execute(
        `codeAgentInitAnswer_${params.job_id}`,
        signal => requestApi.post('console/v1/jobs/coding_agent/show_init_answer', params, {signal})
    );
}


/** 任务运行结果数据查询 */
export async function getStageResultInfo(
    job_id: string, stage: string, params: fetchStageInfoParams, options?: {signal?: AbortSignal}
): Promise<ApiResponse> {
    return requestManager.execute(
        `getStageResultInfo_${job_id}_${stage}`,
        signal => requestApi.post(
            `console/v1/jobs/stage/detail?job_id=${job_id}&stage=${stage}`,
            params,
            {...options, signal}
        )
    );
}

/** 获取阶段全量解分数 */
export async function getStageScore(job_id: string, stage: string): Promise<ApiResponse> {
    return requestManager.execute(
        `getStageScore_${job_id}_${stage}`,
        signal => requestApi.post('console/v1/jobs/stage/score', {job_id, stage}, {signal})
    );
}

/** 任务演化阶段重跑 续跑 全量解信息 */
export async function getEvolveStageRunSolutions(
    job_id: string, params: fetchStageInfoParams, options?: {signal?: AbortSignal}
): Promise<ApiResponse> {
    return requestApi.post(
        `api/v1/jobs/stage/solution/recommend?job_id=${job_id}`,
        params,
        options
    );
}

/**
 * 任务重新配置相关接口
 * 更新配置 rerunJobUpdateConfig
 * 取消 rerunJobConfigCancel
 * 检查可行性 rerunJobConfigCheck
 * 启用 rerunJobConfigEnable
 */
export async function rerunJobUpdateConfig(formData: FormData, job_id: string): Promise<ApiResponse> {
    return requestManager.execute(
        `rerunJobUpdateConfig_${job_id}`,
        signal => requestApi.post('console/v1/jobs/config_update', formData,
            {headers: {'Content-Type': 'multipart/form-data'}, signal})
    );
}
export async function rerunJobConfigCancel(job_id: string): Promise<ApiResponse> {
    return requestManager.execute(
        `rerunJobConfigCancel_${job_id}`,
        signal => requestApi.post('console/v1/jobs/config_cancel', {job_id}, {signal})
    );
}

export async function rerunJobConfigCheck(job_id: string): Promise<ApiResponse> {
    return requestManager.execute(
        `rerunJobConfigCheck_${job_id}`,
        signal => requestApi.post('console/v1/jobs/config_check', {job_id}, {signal})
    );
}
export async function rerunJobConfigEnable(job_id: string): Promise<ApiResponse> {
    return requestManager.execute(
        `rerunJobConfigEnable_${job_id}`,
        signal => requestApi.post('console/v1/jobs/config_enable', {job_id}, {signal})
    );
}

export async function getStageSolutionDetail(params): Promise<ApiResponse> {
    return requestManager.execute(
        `getStageSolutionDetail_${params.job_id}`,
        signal => requestApi.post('console/v1/jobs/stage/solution/detail', params, {signal})
    );
}

export async function postRecovery(job_id: string): Promise<ApiResponse> {
    return requestManager.execute(
        `postRecovery_${job_id}`,
        signal => requestApi.post('console/v1/jobs/recovery', {job_id}, {signal})
    );
}

export async function copyJob(copy_job_id: string): Promise<ApiResponse> {
    return requestManager.execute(
        `copyJob_${copy_job_id}`,
        signal => requestApi.get('console/v1/jobs/copy', {copy_job_id}, {signal})
    );
}

export async function checkStatusJob(
    {check_key, status}: {check_key: string, status: boolean}
): Promise<ApiResponse> {
    return requestManager.execute(
        `checkStatusJob${check_key}`,
        signal => requestApi.put('console/v1/users/check_status', {check_key, status}, {signal})
    );
}

interface JobCodePackageParams {
    job_id: string;
    s_id?: string[];
    status?: number[];
}

type JobCodePackageBodyParams = Omit<JobCodePackageParams, 'job_id'>;

export interface NodeParams {
   sid: string;
   sort: number;
}

interface GenerateJobCodePackageParams {
    job_id: string;
    s_id: NodeParams[];
}

type GenerateJobCodePackageBodyParams = Omit<GenerateJobCodePackageParams, 'job_id'>;

/** 获取任务代码包列表 */
export async function fetchJobCodePackages(params: JobCodePackageParams): Promise<ApiResponse> {
    const {job_id, ...bodyParams}: {job_id: string} & JobCodePackageBodyParams = params;
    return requestManager.execute(
        `fetchJobCodePackages_${job_id}`,
        signal => requestApi.post(`console/v1/code_pack/list?job_id=${job_id}`, bodyParams, {signal})
    );
}

/** 生成任务代码包 */
export async function generateJobCodePackage(params: GenerateJobCodePackageParams): Promise<ApiResponse> {
    const {job_id, ...bodyParams}: {job_id: string} & GenerateJobCodePackageBodyParams = params;
    return requestApi.post(`console/v1/code_pack/initial?job_id=${job_id}`, bodyParams);
}
